import StickyFooter from "@components/quiz/StickyFooter"
import { vmColor } from "@helpers/themeColor"
import { UIEvent, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getMindMapsPerMonth } from "../personalization"
import StepHeading from "./StepHeading"

const MIN_MINUTES = 5
const MAX_MINUTES = 30
const DEFAULT_MINUTES = 15
const ITEM_HEIGHT_PX = 52
const VISIBLE_ITEMS = 5

const MINUTE_OPTIONS = Array.from(
  { length: MAX_MINUTES - MIN_MINUTES + 1 },
  (_, index) => MIN_MINUTES + index,
)

interface Props {
  id: string
  onAnswer: (answers: string[]) => void
}

export default function DailyGoalStep({ id, onAnswer }: Props) {
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES)
  const wheelRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    wheelRef.current?.scrollTo({
      top: (DEFAULT_MINUTES - MIN_MINUTES) * ITEM_HEIGHT_PX,
    })
  }, [])

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const index = Math.round(event.currentTarget.scrollTop / ITEM_HEIGHT_PX)

    setMinutes(
      MINUTE_OPTIONS[Math.min(Math.max(index, 0), MINUTE_OPTIONS.length - 1)],
    )
  }

  const selectMinutes = (value: number) =>
    wheelRef.current?.scrollTo({
      behavior: "smooth",
      top: (value - MIN_MINUTES) * ITEM_HEIGHT_PX,
    })

  const mindMapsPerMonth = getMindMapsPerMonth(minutes)
  const padding = ((VISIBLE_ITEMS - 1) / 2) * ITEM_HEIGHT_PX

  return (
    <div className="flex grow flex-col gap-6 pt-4">
      <StepHeading
        headingKey={`pages.onboarding.steps.${id}.heading`}
        subheadingKey={`pages.onboarding.steps.${id}.subheading`}
      />

      <div className="relative grid grow animate-fade-up grid-cols-[1fr_auto_1fr] items-center gap-3 [animation-delay:150ms]">
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/2 w-36 -translate-x-[40%] -translate-y-1/2 rounded-card bg-vm-surface shadow-card"
          style={{ height: ITEM_HEIGHT_PX + 8 }}
        />

        <div className="relative w-32 justify-self-end rounded-vm-md border border-vm-primary/20 bg-vm-primary-soft px-3 py-2 text-sm font-medium leading-snug text-vm-primary-text">
          <span className="block tabular-nums" key={mindMapsPerMonth}>
            {t(`pages.onboarding.steps.${id}.tooltip`, {
              count: mindMapsPerMonth,
            })}
          </span>
          <span className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rotate-45 border-r border-t border-vm-primary/20 bg-vm-primary-soft" />
        </div>

        <div
          className="relative overflow-y-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          onScroll={handleScroll}
          ref={wheelRef}
          style={{
            height: ITEM_HEIGHT_PX * VISIBLE_ITEMS,
            maskImage:
              "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
            scrollSnapType: "y mandatory",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)",
          }}
        >
          <div style={{ paddingBottom: padding, paddingTop: padding }}>
            {MINUTE_OPTIONS.map((value) => {
              const distance = Math.abs(value - minutes)

              return (
                <button
                  className="flex w-16 items-center justify-center font-bold tabular-nums transition-[transform,color,opacity] duration-150"
                  key={value}
                  onClick={() => selectMinutes(value)}
                  style={{
                    color: vmColor("primary-text", distance === 0 ? 1 : 0.6),
                    fontSize: distance === 0 ? 36 : 28,
                    height: ITEM_HEIGHT_PX,
                    opacity: Math.max(1 - distance * 0.25, 0.2),
                    scrollSnapAlign: "center",
                  }}
                  type="button"
                >
                  {value}
                </button>
              )
            })}
          </div>
        </div>

        <span className="relative justify-self-start text-xl font-bold text-vm-primary-text">
          {t(`pages.onboarding.steps.${id}.unit`)}
        </span>
      </div>

      <StickyFooter>
        <button
          className="vm-button-primary"
          onClick={() => onAnswer([String(minutes)])}
          type="button"
        >
          {t("shared.continue")}
        </button>
      </StickyFooter>
    </div>
  )
}
